import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader, Undo2 } from 'lucide-react';
import { Row } from '../settingsPrimitives';
import { Modal } from '../ui/Modal';
import { useNotificationStore } from '../../store/notificationStore';
import { requestWithdrawal, createBillingPortalSession, billingErrorMessage } from '../../utils/apiClient';
import { openExternal } from '../../utils/openExternal';
import './WithdrawalSection.css';

/**
 * § 13a FAGG "Vertrag widerrufen" (BGBl. I 59/2026; every online consumer
 * contract concluded after 30.09.2026).
 *
 * The law fixes the two labels — "Vertrag widerrufen" to open it and
 * "Widerruf bestätigen" to send it — and wants the entry button legible,
 * prominent and there for the WHOLE withdrawal period. So the button is shown
 * for every paid subscription, unconditionally: the app does not try to work
 * out whether the 14 days are over (it does not know when the contract was
 * concluded to the minute, the server does). The server answers with
 * `eligible` and this dialog explains either outcome; a late withdrawal is
 * still RECEIVED and confirmed, and the user is pointed at the normal cancel.
 *
 * The receipt email (the durable-medium confirmation) is sent by the backend,
 * not from here.
 */

/** "17 October 2026, 14:05 CEST" in the user's language, Vienna time (the receipt email's zone). */
export function formatReceiptTime(iso, language) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleString(language || 'en', {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/Vienna', timeZoneName: 'short',
    });
  } catch {
    return date.toISOString();
  }
}

/** A refund `{ amount_cents, currency }` as "€19.99" / "19,99 €"; '' when there is none. */
export function formatRefund(refund, language) {
  const cents = refund?.amount_cents;
  if (!Number.isInteger(cents) || cents <= 0) return '';
  const currency = String(refund.currency || 'eur').toUpperCase();
  try {
    return new Intl.NumberFormat(language || 'en', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function formatStartDate(iso, language) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(language || 'en', { day: 'numeric', month: 'long', year: 'numeric' });
}

function WithdrawalDialog({ isOpen, onClose, planName, billingCycle, email, startedAt }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const close = () => {
    if (sending) return;
    setReason('');
    setResult(null);
    onClose();
  };

  const submit = async () => {
    if (sending) return;
    setSending(true);
    try {
      const data = await requestWithdrawal(reason);
      setResult(data || { status: 'received' });
    } catch (err) {
      useNotificationStore.getState().addNotification({
        type: 'error',
        title: t('legal.withdrawal.errorTitle'),
        message: billingErrorMessage(err, t('legal.withdrawal.error')),
      });
    } finally {
      setSending(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const url = await createBillingPortalSession();
      await openExternal(url);
    } catch (err) {
      useNotificationStore.getState().addNotification({
        type: 'error',
        title: t('legal.withdrawal.errorTitle'),
        message: billingErrorMessage(err, t('legal.withdrawal.portalError')),
      });
    } finally {
      setPortalLoading(false);
    }
  };

  const cycleLabel = billingCycle === 'annual' ? t('plans.billing.annual') : t('plans.billing.monthly');
  const startLabel = formatStartDate(startedAt, language) || '—';

  let body;
  if (result) {
    const receivedAt = formatReceiptTime(result.received_at, language);
    const eligible = result.eligible === true;
    const refund = formatRefund(result.refund, language);
    body = (
      <div className="withdrawal-result" data-testid="withdrawal-result" role="status">
        <div className="withdrawal-result-head">
          <CheckCircle2 size={18} aria-hidden />
          <strong>{t('legal.withdrawal.receivedTitle')}</strong>
        </div>
        {receivedAt && <p data-testid="withdrawal-received-at">{t('legal.withdrawal.receivedAt', { date: receivedAt })}</p>}
        <p data-testid="withdrawal-email-sent">
          {email ? t('legal.withdrawal.emailSent', { email }) : t('legal.withdrawal.emailSentNoAddress')}
        </p>
        {eligible ? (
          <>
            <p data-testid="withdrawal-cancelled">
              {result.cancelled ? t('legal.withdrawal.cancelled') : t('legal.withdrawal.processing')}
            </p>
            <p data-testid="withdrawal-refund">
              {refund ? t('legal.withdrawal.refund', { amount: refund }) : t('legal.withdrawal.noRefund')}
            </p>
          </>
        ) : (
          <>
            <p data-testid="withdrawal-not-eligible">{t('legal.withdrawal.notEligible')}</p>
            <button
              type="button"
              className="settings-action-btn"
              onClick={openPortal}
              disabled={portalLoading}
              data-testid="withdrawal-cancel-portal"
            >
              {portalLoading && <Loader size={13} className="animate-spin" aria-hidden />}
              {t('legal.withdrawal.cancelViaPortal')}
            </button>
          </>
        )}
        {result.withdrawal_id != null && (
          <p className="withdrawal-ref">{t('legal.withdrawal.reference', { id: String(result.withdrawal_id) })}</p>
        )}
        <div className="withdrawal-actions">
          <button type="button" className="settings-action-btn" onClick={close}>
            {t('legal.withdrawal.close')}
          </button>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="withdrawal-form">
        <p>{t('legal.withdrawal.intro')}</p>
        <dl className="withdrawal-ident" data-testid="withdrawal-ident">
          <dt>{t('legal.withdrawal.fieldEmail')}</dt>
          <dd>{email || '—'}</dd>
          <dt>{t('legal.withdrawal.fieldContract')}</dt>
          <dd>{t('legal.withdrawal.contractValue', { plan: planName, cycle: cycleLabel })}</dd>
          <dt>{t('legal.withdrawal.fieldStart')}</dt>
          <dd>{startLabel}</dd>
        </dl>
        <label className="withdrawal-reason">
          <span>{t('legal.withdrawal.reasonLabel')}</span>
          <textarea
            className="modal-input"
            rows={3}
            maxLength={2000}
            value={reason}
            placeholder={t('legal.withdrawal.reasonPlaceholder')}
            onChange={(e) => setReason(e.target.value)}
            disabled={sending}
          />
        </label>
        <p className="withdrawal-note">{t('legal.withdrawal.confirmNote')}</p>
        <div className="withdrawal-actions">
          <button type="button" className="settings-action-btn" onClick={close} disabled={sending}>
            {t('legal.withdrawal.keep')}
          </button>
          <button
            type="button"
            className="btn-destructive"
            onClick={submit}
            disabled={sending}
            data-testid="withdrawal-confirm"
          >
            {sending && <Loader size={13} className="animate-spin" aria-hidden />}
            {t('legal.withdrawal.confirm')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t('legal.withdrawal.title')}
      preventBackdropClose={sending}
      preventEscapeClose={sending}
      size="lg"
    >
      {body}
    </Modal>
  );
}

/** The entry row: a plain, always-visible "Withdraw from contract" button plus its dialog. */
export function WithdrawContractRow({ planName, billingCycle, email, startedAt }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Row label={t('legal.withdrawal.rowLabel')} desc={t('legal.withdrawal.rowDesc')}>
        <button
          type="button"
          className="settings-action-btn withdrawal-entry-btn"
          onClick={() => setOpen(true)}
          data-testid="withdrawal-open"
        >
          <Undo2 size={13} aria-hidden />
          {t('legal.withdrawal.button')}
        </button>
      </Row>
      <WithdrawalDialog
        isOpen={open}
        onClose={() => setOpen(false)}
        planName={planName}
        billingCycle={billingCycle}
        email={email}
        startedAt={startedAt}
      />
    </>
  );
}
